<template>
  <div id="top" class="claim-registry-page claim-page-shell">
    <div class="claim-registry-intro">
      <UiPageHeader
        title="Claims"
        description="Review provider claim batches and move each submission through the claims workflow."
      >
        <template #actions>
          <router-link :to="{ name: 'claimAdd' }" class="claim-add-link">
            <FilePlus2 :size="17" aria-hidden="true" />
            <span>Add claim</span>
          </router-link>
        </template>
      </UiPageHeader>
    </div>

    <section class="claim-workbench sba-slide-in" aria-label="Claims cluster register">
      <div class="claim-registry-toolbar">
        <div class="claim-review-summary" aria-live="polite">
          <strong v-if="isReady">
            {{ clusters.length }} {{ clusters.length === 1 ? "cluster" : "clusters" }} in this view
          </strong>
          <strong v-else>Loading claim clusters</strong>
          <span>Clinical and operational review queue</span>
        </div>
        <button
          type="button"
          class="claim-search-toggle"
          aria-controls="claim-filter-panel"
          :aria-expanded="showSearchFilters"
          @click="toggleSearchFilters"
        >
          <span>{{ showSearchFilters ? "Close filters" : "Filter" }}</span>
          <i class="material-icons" aria-hidden="true">{{ showSearchFilters ? "close" : "filter_list" }}</i>
        </button>
      </div>

      <div id="claim-filter-panel" class="claim-filter-shell" v-show="showSearchFilters">
        <div class="claim-filter-panel">
          <FilterClaim @search="handleSearch" />
        </div>
      </div>

      <div class="claim-search-summary">
        <span class="summary-label">Current view</span>
        <div class="summary-content" v-if="searchTitle" v-html="searchTitle"></div>
        <div class="summary-content" v-else>
          <span class="summary-chip">All claims clusters</span>
        </div>
      </div>

    <!-- <div class="row">
      <div class="col s12"> -->

        <!-- Vetting Marker -->
        <div id="vet"></div>

        <!-- Claims -->
        <LoadingDiv v-if="!isReady" />

        <div v-else>
          <section class="claim-cluster-section" v-if="clusters.length || isLoading" aria-label="Claims clusters">
            <div class="claim-cluster-list">
              <div class="claim-list-progress" v-if="isLoading" aria-label="Refreshing claims">
                <span></span>
              </div>

              <div class="claim-cluster-head" aria-hidden="true">
                <span>No.</span>
                <span>Provider</span>
                <span>Sent</span>
                <span>Approved</span>
                <span>Period</span>
                <span>Status</span>
                <span></span>
              </div>

              <article class="claim-cluster-row" v-for="(cluster, index) in clusters" :key="cluster.id">
                <div class="claim-row-number">{{ numberBadge(index) }}</div>

                <div class="claim-provider-cell">
                  <div class="claim-provider-title">
                    <span class="claim-flags" v-if="hasFlags(cluster)" aria-label="Issues raised">
                      <i class="material-icons" aria-hidden="true">flag</i>
                    </span>
                    <span>{{ capWords(cluster.provider.name) }}</span>
                  </div>
                  <span class="claim-count-text" v-if="cluster.status !== 'q'">
                    {{ cluster.encounters_count }} {{ cluster.encounters_count === 1 ? "claim" : "claims" }}
                  </span>
                  <div class="claim-related-badges" v-if="relatedBadges(cluster).length">
                    <span
                      v-for="badge in relatedBadges(cluster)"
                      :key="badge.key"
                      class="claim-related-badge"
                      :class="`is-${badge.key}`"
                    >{{ badge.label }}</span>
                  </div>
                </div>

                <div class="claim-data-cell">
                  <span class="claim-mobile-label">Sent</span>
                  <span class="claim-amount is-sent">{{ formatMoney(cluster.status === 'q' ? 0 : cluster.encounters_total_sent) }}</span>
                </div>

                <div class="claim-data-cell">
                  <span class="claim-mobile-label">Approved</span>
                  <span class="claim-amount is-approved" v-if="cluster.status === 'r' || cluster.status === 'v'">
                    {{ formatMoney(cluster.encounters_total_approved) }}
                  </span>
                  <span class="claim-muted-value" v-else>Not reviewed</span>
                </div>

                <div class="claim-period-cell">
                  <span class="claim-mobile-label">Period</span>
                  <strong>{{ toMonthYear(cluster.month_year) }}</strong>
                  <span>{{ cluster.scheme.name }}</span>
                </div>

                <div class="claim-data-cell">
                  <span class="claim-mobile-label">Status</span>
                  <span class="claim-status" :class="statusClass(cluster)">{{ paymentStatus(cluster) }}</span>
                </div>

                <div class="claim-action-cell">
                  <button
                    v-if="cluster.status === 'q'"
                    type="button"
                    class="claim-row-action is-locked"
                    aria-label="Claim cluster is locked"
                    disabled
                  >
                    <i class="material-icons" aria-hidden="true">lock_outline</i>
                  </button>
                  <button
                    v-else
                    type="button"
                    class="claim-row-action"
                    :aria-label="cluster.status === 'p' ? 'Review claim cluster' : 'View claim cluster details'"
                    @click="handleEdit(cluster)"
                  >
                    <i class="material-icons" aria-hidden="true">chevron_right</i>
                  </button>
                </div>
              </article>
            </div>
          </section>
          <div class="section claim-empty-state" v-if="!clusters.length && !isLoading">
            <div class="claim-empty-icon">
              <i class="material-icons">fact_check</i>
            </div>
            <div class="claim-empty-copy">
              <h6>No claims found</h6>
              <p>There are no claim clusters matching the current claims view.</p>
              <span>Try another scheme, period, or status filter.</span>
            </div>
          </div>

          <PaginationDiv class="claim-pagination" :payLoad="allPages" @newPage="handlePage" v-else />
        </div>
      <!-- </div>
    </div> -->
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import FilterClaim from "@/components/claim/FilterClaimComponent.vue";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import { FilePlus2 } from "@lucide/vue";
import {
  capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";
import {
  // grossTotalSentFx,
  // grossTotalApprovedFx,
  paymentStatusFx,
} from "@/composables/calFunction/cal";
import { getCache, addCache } from "@/composables/cache/cache";

export default {
  name: "claimHome",
  components: { FilePlus2, LoadingDiv, PaginationDiv, FilterClaim, UiPageHeader },
  setup() {
    const response = ref(null);
    const isReady = ref(false);
    const isLoading = ref(false);
    const error = ref(null);
    const currUrl = ref(null);
    const currHook = ref(null);
    const searchTitle = ref(null);
    const UIUpdate = ref(true);
    const showSearchFilters = ref(false);
    const currentListUrl = ref(null);
    const pendingSyncRefresh = ref(false);

    // vue router
    const router = useRouter();

    // start
    onMounted(() => {
      // page name
      document.title = "All Claims Cluster";
      window.scrollTo({ top: 0, behavior: "auto" });
    });

    // starter
    onMounted(() => {
      // get claims from oneProvider
      (async () => {
        const { res } = await postItem("integration/oneprovider/claims/sync", { force: true });

        if (res.value) {
          if (Number(res.value.data?.imported || 0) > 0 && UIUpdate.value) {
            // reload page
            reloadCurrentClaims();
          }
        }
  
      })();
    });

    const toggleSearchFilters = () => {
      showSearchFilters.value = !showSearchFilters.value;
    };

    const hasFlags = (cluster) => {
      return ["supervisor", "hod", "compliance"].some((type) => flagCount(cluster, type) > 0);
    };

    const flagCount = (cluster, type) => {
      const countKey = `${type}_flags_count`;
      const flagKey = `${type}_flag`;

      if (cluster?.[countKey] !== undefined && cluster?.[countKey] !== null) {
        return Number(cluster[countKey]) || 0;
      }

      if (!Array.isArray(cluster?.encounters)) {
        return 0;
      }

      return cluster.encounters.filter((x) => x?.[flagKey] == 'y').length;
    };

    const statusClass = (cluster) => {
      const status = paymentStatus(cluster)?.toLowerCase();
      if (status === 'paid') return 'is-paid';
      if (status === 'pending') return 'is-pending';
      if (status === 'unsubmitted') return 'is-unsubmitted';
      return 'is-other';
    };

    const relatedBadges = (cluster) => {
      const badges = [];
      const supplementaryCount = Number(cluster?.related_supplementary_count || 0);
      const reconciliationCount = Number(cluster?.related_reconciliation_count || 0);

      if (supplementaryCount > 0) {
        badges.push({
          key: "supplementary",
          label: `Supplementary${supplementaryCount > 1 ? ` (${supplementaryCount})` : ""}`,
        });
      }

      if (reconciliationCount > 0) {
        badges.push({
          key: "reconciliation",
          label: `Reconciliation${reconciliationCount > 1 ? ` (${reconciliationCount})` : ""}`,
        });
      }

      return badges;
    };

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // total calculations (gross sent)
    // const grossTotalSent = (encounters) => {
    //   return grossTotalSentFx(encounters);
    // };

    // total calculations (gross approved)
    // const grossTotalApproved = (encounters, db) => {
    //   return grossTotalApprovedFx(encounters, db);
    // };

    // convert to Month & Year
    const toMonthYear = (val) => {
      let parts = val.split("_");
      let month = parseInt(parts[0]);
      let year = parseInt(parts[1]);
      let date = new Date(year, month - 1); // months are zero-indexed in JavaScript
      let monthName = date.toLocaleString("default", { month: "short" });
      return monthName + " " + year;
    };

    // payment status
    const paymentStatus = (cluster) => {
      return paymentStatusFx(cluster);
    };

    const formatMoney = (value) => {
      const amount = Number(value || 0);
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number.isFinite(amount) ? amount : 0);
    };

    // get function
    const getItemsFromApi = (table, options = {}) => {
      if (!table) {
        return;
      }

      currentListUrl.value = table;

      // get item from store (cache)
      let cache = getCache(table);
      // load cache
      if (cache && !options.skipCache) {
        response.value = cache;
        isReady.value = true;
      }
      // get current from server
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
          // add cache
          addCache(table, response.value);
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    // handle search (emitted from component)
    const handleSearch = (val) => {
      // start loading
      isLoading.value = true;
      // set current Url
      currUrl.value = val.currUrl;
      // set current Hook
      currHook.value = val.currHook;
      // set search title
      searchTitle.value = val.searchTitle;
      const skipCache = pendingSyncRefresh.value;
      // claim sync may finish before the default filter initializes
      pendingSyncRefresh.value = false;
      // get items from function
      getItemsFromApi(currUrl.value, { skipCache });
    };

    const reloadCurrentClaims = () => {
      const targetUrl = currentListUrl.value || currUrl.value;

      if (!targetUrl) {
        pendingSyncRefresh.value = true;
        return;
      }

      pendingSyncRefresh.value = false;
      isLoading.value = true;
      getItemsFromApi(targetUrl, { skipCache: true });
    };

    // handle edit
    const handleEdit = (cluster) => {
      router.push({ name: "claimView", params: { clusterId: cluster.id } });
    };

    // switch page
    const handlePage = (page) => {
      // start laoding
      isLoading.value = true;
      // go back up
      router.push({ hash: "#top" });
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`);
      // kill update
      UIUpdate.value = false;
    };

    // output results
    const clusters = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data; // get data portion
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    return {
      toggleSearchFilters,
      hasFlags,
      flagCount,
      statusClass,
      relatedBadges,
      showSearchFilters,
      capWords,
      numberBadge,
      // grossTotalSent,
      // grossTotalApproved,
      toMonthYear,
      paymentStatus,
      formatMoney,
      handlePage,
      handleSearch,
      handleEdit,
      isReady,
      isLoading,
      error,
      searchTitle,
      clusters,
      allPages,
    };
  },
};
</script>

<style scoped>
.claim-page-shell {
  box-sizing: border-box;
  margin: 0 auto;
  max-width: var(--ui-content-width);
  padding: 2rem 2rem 3rem;
  width: 100%;
}

.claim-workbench {
  color: var(--ui-foreground);
  min-width: 0;
}

.claim-registry-intro {
  border-bottom: 1px solid var(--ui-border);
  margin-bottom: 2rem;
  padding-bottom: 1.75rem;
}

.claim-registry-intro :deep(.ui-page-header) {
  margin-bottom: 0;
}

.claim-registry-intro :deep(.ui-page-header__title) {
  font-size: clamp(1.75rem, 2.1vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.025em;
}

.claim-registry-intro :deep(.ui-page-header__description) {
  font-size: 1rem;
  line-height: 1.55;
}

.claim-add-link {
  align-items: center;
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  border-radius: calc(var(--ui-radius) - 2px);
  color: var(--ui-primary-foreground);
  display: inline-flex;
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 0.9rem;
  text-decoration: none;
}

.claim-add-link:hover {
  filter: brightness(0.95);
}

.claim-add-link:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.claim-registry-toolbar,
.claim-filter-shell,
.claim-search-summary,
.claim-cluster-section,
.claim-empty-state {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.claim-registry-toolbar {
  align-items: center;
  background: var(--ui-background);
  border: 0 !important;
  border-bottom: 1px solid var(--ui-border) !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  gap: 1.5rem;
  justify-content: space-between;
  padding: 0 0 1.25rem !important;
}

.claim-review-summary {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.claim-review-summary strong {
  color: var(--ui-foreground);
  font-size: 1rem;
  font-weight: 650;
  line-height: 1.4;
}

.claim-review-summary span {
  color: var(--ui-muted-foreground);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.claim-search-toggle {
  align-items: center;
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  border-radius: calc(var(--ui-radius) - 2px);
  color: var(--ui-primary-foreground);
  cursor: pointer;
  display: inline-flex;
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 0.9rem;
}

.claim-search-toggle i {
  font-size: 1rem;
}

.claim-search-toggle:focus-visible,
.claim-row-action:focus-visible {
  outline: 3px solid rgba(14, 116, 144, 0.24);
  outline-offset: 2px;
}

.claim-filter-shell {
  background: var(--ui-background);
  border-bottom: 1px solid var(--ui-border);
  padding: 1.25rem 0;
  position: relative;
  z-index: 5;
}

.claim-filter-panel {
  background: transparent;
  border: 0;
  box-shadow: none;
  overflow: visible;
  padding: 0;
  width: 100%;
}

.claim-filter-panel :deep(.sectionx),
.claim-filter-panel :deep(form) {
  margin: 0;
  padding: 0;
}

.claim-filter-panel :deep(.sectionx > br) {
  display: none;
}

.claim-filter-panel :deep(.row) {
  align-items: start;
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
}

.claim-filter-panel :deep(.col) {
  border: 0;
  min-width: 0;
  padding: 0 !important;
  width: 100% !important;
}

.claim-filter-panel :deep(.col:focus-within) {
  position: relative;
  z-index: 3;
}

.claim-filter-panel :deep(.col > label) {
  color: var(--ui-foreground);
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.35;
  margin-bottom: 0.4rem;
}

.claim-filter-panel :deep(nav),
.claim-filter-panel :deep(.nav-wrapper),
.claim-filter-panel :deep(.input-field) {
  background: transparent !important;
  box-shadow: none !important;
  height: auto;
  line-height: normal;
  margin: 0;
  min-height: 2.75rem;
  overflow: visible !important;
  width: 100%;
}

.claim-filter-panel :deep(.input-field) {
  position: relative;
}

.claim-filter-panel :deep(.input-field > input),
.claim-filter-panel :deep(.select-wrapper input.select-dropdown) {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-input) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  box-shadow: none !important;
  box-sizing: border-box;
  color: var(--ui-foreground) !important;
  font-size: 0.875rem;
  font-weight: 400;
  height: 2.75rem !important;
  line-height: 2.75rem;
  margin: 0;
  padding: 0 2.5rem 0 0.75rem !important;
  width: 100%;
}

.claim-filter-panel :deep(.input-field > input[type="search"]::-webkit-search-cancel-button) {
  -webkit-appearance: none;
  display: none;
}

.claim-filter-panel :deep(.input-field > input:focus) {
  border-color: var(--ui-ring) !important;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ui-ring) 18%, transparent) !important;
}

.claim-filter-panel :deep(.input-field > .material-icons) {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--ui-muted-foreground) !important;
  cursor: pointer;
  display: inline-flex;
  font-size: 1.1rem;
  height: 2.75rem;
  justify-content: center;
  line-height: 1;
  margin: 0;
  position: absolute;
  right: 0.25rem;
  text-decoration: none !important;
  top: 0;
  width: 2.25rem;
  z-index: 2;
}

.claim-filter-panel :deep(.input-field > .material-icons:hover) {
  color: var(--ui-foreground) !important;
}

.claim-filter-panel :deep(.native-autocomplete) {
  background: var(--ui-popover) !important;
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: var(--ui-shadow-overlay);
  box-sizing: border-box;
  left: 0;
  margin: 0;
  max-height: min(16rem, 50vh);
  min-width: 0;
  overflow-y: auto;
  padding: 0.3rem;
  right: 0;
  top: calc(100% + 0.25rem);
  width: 100%;
  z-index: 220;
}

.claim-filter-panel :deep(.native-autocomplete li) {
  margin: 0;
  min-height: 0;
}

.claim-filter-panel :deep(.native-autocomplete li > :is(button, span)) {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: calc(var(--ui-radius) - 4px);
  color: var(--ui-popover-foreground) !important;
  cursor: pointer;
  display: flex;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.35;
  min-height: 2.35rem;
  padding: 0.55rem 0.65rem;
  text-align: left;
  width: 100%;
}

.claim-filter-panel :deep(.native-autocomplete li > button:hover),
.claim-filter-panel :deep(.native-autocomplete li > button:focus-visible),
.claim-filter-panel :deep(.native-autocomplete li > button[aria-selected="true"]) {
  background: var(--ui-accent) !important;
  color: var(--ui-accent-foreground) !important;
  outline: 0;
}

.claim-filter-panel :deep(.mobile-remote-sheet-field) {
  min-height: 2.75rem;
}

.claim-filter-panel :deep(.mobile-remote-trigger) {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-input) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  color: var(--ui-foreground) !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  height: 2.75rem !important;
  line-height: normal !important;
  padding: 0 0.25rem 0 0.75rem !important;
}

.claim-filter-panel :deep(.mobile-remote-trigger-main),
.claim-filter-panel :deep(.mobile-remote-trigger-icon) {
  height: 2.65rem !important;
  line-height: normal !important;
}

.claim-filter-panel :deep(.dropdown-content) {
  background: #ffffff;
  border: 1px solid #d7e1e7;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
  left: 0 !important;
  margin: -1px 0 0 !important;
  max-height: 240px;
  overflow-y: auto;
  position: absolute !important;
  top: 100% !important;
  transform: none !important;
  width: 100% !important;
  z-index: 30;
}

.claim-filter-panel :deep(.dropdown-content li) {
  min-height: 42px;
}

.claim-filter-panel :deep(.dropdown-content li > span) {
  color: #172033;
  font-size: 0.9rem;
  line-height: 1.35;
  padding: 11px 15px;
}

.claim-search-summary {
  align-items: center;
  background: var(--ui-background) !important;
  border: 0 !important;
  border-bottom: 1px solid var(--ui-border) !important;
  border-radius: 0 !important;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  min-height: 3.5rem;
  padding: 0.75rem 0 !important;
}

.summary-label {
  color: var(--ui-muted-foreground);
  flex: 0 0 auto;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.summary-content {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: flex-end;
  min-width: 0;
  text-align: right;
}

.claim-search-summary :deep(.chip),
.summary-chip {
  background: var(--ui-secondary) !important;
  border: 1px solid var(--ui-border) !important;
  border-radius: 999px !important;
  color: var(--ui-secondary-foreground) !important;
  font-size: 0.75rem !important;
  font-style: normal;
  font-weight: 500 !important;
  height: auto !important;
  line-height: 1.25 !important;
  margin: 0 !important;
  padding: 0.35rem 0.65rem !important;
  white-space: nowrap;
}

.claim-search-summary :deep(.chip:first-child) {
  background: transparent !important;
  color: var(--ui-muted-foreground) !important;
}

.claim-search-summary :deep(.chip i) {
  font-size: 0.8rem;
  line-height: 1;
}

.claim-cluster-section {
  margin-top: 0;
}

.claim-cluster-list {
  background: transparent;
  border: 0;
  border-radius: 0;
  overflow: visible;
  position: relative;
}

.claim-list-progress {
  background: #e5edf1;
  height: 2px;
  left: 0;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
}

.claim-list-progress span {
  animation: claimListLoading 1.1s ease-in-out infinite;
  background: #0f766e;
  display: block;
  height: 100%;
  width: 36%;
}

@keyframes claimListLoading {
  from {
    transform: translateX(-105%);
  }

  to {
    transform: translateX(285%);
  }
}

.claim-cluster-head,
.claim-cluster-row {
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-template-columns: 2.5rem minmax(14rem, 1.7fr) minmax(9rem, 1fr) minmax(10rem, 1.1fr) minmax(7rem, 0.75fr) 8.25rem 2.75rem;
}

.claim-cluster-head {
  background: transparent;
  border-bottom: 1px solid var(--ui-border);
  border-top: 0;
  color: var(--ui-muted-foreground);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0;
  padding: 0.8rem 0.75rem;
  text-transform: uppercase;
}

.claim-cluster-head > span {
  padding: 0;
}

.claim-cluster-row {
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--ui-border);
  border-radius: 0;
  box-shadow: none;
  margin: 0;
  padding: 1.15rem 0.75rem;
  transition: background-color 160ms ease;
}

.claim-cluster-row:hover,
.claim-cluster-row:focus-within {
  background: color-mix(in srgb, var(--ui-muted) 45%, transparent);
}

.claim-cluster-row > div {
  min-width: 0;
  padding: 0;
}

.claim-row-number {
  color: var(--ui-muted-foreground);
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: left;
}

.claim-provider-title {
  align-items: flex-start;
  color: var(--ui-foreground);
  display: flex;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 0.5rem;
  line-height: 1.4;
}

.claim-flags {
  color: #e11d48;
  display: inline-flex;
  flex: 0 0 auto;
}

.claim-flags i {
  font-size: 17px;
}

.claim-count-text {
  color: var(--ui-muted-foreground);
  display: block;
  font-size: 0.75rem;
  line-height: 1.4;
  margin-top: 0.3rem;
}

.claim-related-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 7px;
}

.claim-related-badge {
  border: 1px solid #d7e1e7;
  border-radius: 999px;
  color: #52677a;
  display: inline-flex;
  font-size: 0.64rem;
  font-weight: 800;
  line-height: 1;
  padding: 4px 7px;
  text-transform: uppercase;
}

.claim-related-badge.is-supplementary {
  background: #f0fdf4;
  border-color: #bbddc7;
  color: #18703d;
}

.claim-related-badge.is-reconciliation {
  background: #eff6ff;
  border-color: #c5d9f2;
  color: #245c94;
}

.claim-data-cell,
.claim-period-cell {
  color: var(--ui-foreground);
  font-size: 0.8125rem;
  line-height: 1.4;
}

.claim-amount {
  border: 1px solid;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.35rem 0.55rem;
  white-space: nowrap;
}

.claim-amount.is-sent {
  background: #fff0f3;
  border-color: #f3b4c0;
  color: #ed1231;
}

.claim-amount.is-approved {
  background: #eaf8f2;
  border-color: #b6dacb;
  color: #008442;
}

.claim-muted-value {
  color: var(--ui-muted-foreground);
  font-size: 0.75rem;
  font-weight: 600;
}

.claim-period-cell strong,
.claim-period-cell > span:last-child {
  display: block;
}

.claim-period-cell strong {
  color: var(--ui-foreground);
  font-weight: 600;
}

.claim-period-cell > span:last-child {
  color: var(--ui-muted-foreground);
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.claim-status {
  background: #f1f5f9;
  border: 1px solid #d7e1e7;
  border-radius: 999px;
  color: #52677a;
  display: inline-flex;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.35rem 0.55rem;
  text-transform: uppercase;
  white-space: nowrap;
}

.claim-status.is-paid {
  background: #eaf8f2;
  border-color: #b6dacb;
  color: #008442;
}

.claim-status.is-pending {
  background: #fff8e6;
  border-color: #ecd9a0;
  color: #855500;
}

.claim-status.is-unsubmitted {
  background: #fff4e8;
  border-color: #edc69d;
  color: #9a4e06;
}

.claim-action-cell {
  display: flex;
  justify-content: center;
}

.claim-row-action {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d7e1e7;
  border-radius: 6px;
  color: #52677a;
  cursor: pointer;
  display: inline-flex;
  height: 2.5rem;
  justify-content: center;
  padding: 0;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease;
  width: 2.5rem;
}

.claim-cluster-row:hover .claim-row-action:not(:disabled),
.claim-cluster-row:focus-within .claim-row-action:not(:disabled) {
  background: #0f172a;
  border-color: #0f172a;
  color: #ffffff;
}

.claim-row-action i {
  font-size: 20px;
  text-decoration: none !important;
}

.claim-row-action.is-locked {
  background: #f8fafc;
  color: #94a3b8;
  cursor: default;
}

.claim-mobile-label {
  display: none;
}

.claim-empty-state {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe4ea;
  border-radius: 7px;
  color: #64748b;
  display: flex;
  gap: 17px;
  margin-top: 18px;
  min-height: 150px;
  padding: 28px 30px;
}

.claim-empty-icon {
  align-items: center;
  background: #eef8f7;
  border: 1px solid #cce5e2;
  border-radius: 7px;
  color: #0f766e;
  display: inline-flex;
  flex: 0 0 auto;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.claim-empty-icon i {
  font-size: 26px;
}

.claim-empty-copy h6 {
  color: #172033;
  font-size: 1rem;
  font-weight: 800;
  margin: 0;
}

.claim-empty-copy p {
  color: #64748b;
  line-height: 1.45;
  margin: 6px 0 0;
}

.claim-empty-copy span {
  color: #8291a3;
  display: block;
  font-size: 0.8rem;
  font-weight: 650;
  margin-top: 8px;
}

.claim-pagination {
  display: block;
  margin-top: 24px;
}

.pointer,
.pointable {
  cursor: default;
}

@media only screen and (max-width: 1100px) {
  .claim-cluster-head,
  .claim-cluster-row {
    gap: 0.75rem;
    grid-template-columns: 2rem minmax(12rem, 1.5fr) minmax(8rem, 0.9fr) minmax(9rem, 1fr) minmax(6rem, 0.7fr) 7.75rem 2.5rem;
  }
}

@media only screen and (max-width: 992px) {
  .claim-page-shell {
    padding-inline: 1.25rem;
  }

  .claim-filter-panel :deep(.row) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .claim-cluster-head {
    display: none;
  }

  .claim-cluster-list {
    border-bottom: 0;
  }

  .claim-cluster-row {
    gap: 12px 24px;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    min-height: 0;
    padding: 20px 64px 20px 22px;
    position: relative;
  }

  .claim-cluster-row > div {
    padding: 0;
  }

  .claim-row-number {
    display: none;
  }

  .claim-provider-cell {
    grid-column: 1 / -1;
  }

  .claim-action-cell {
    position: absolute;
    right: 16px;
    top: 20px;
  }

  .claim-mobile-label {
    color: #8291a3;
    display: block;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    margin-bottom: 6px;
    text-transform: uppercase;
  }
}

@media only screen and (max-width: 600px) {
  .claim-page-shell {
    padding: 1.25rem 1rem 2.5rem;
  }

  .claim-registry-intro {
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
  }

  .claim-registry-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .claim-search-toggle {
    width: 100%;
  }

  .claim-filter-shell {
    padding: 1.25rem 0;
  }

  .claim-filter-panel :deep(.row) {
    grid-template-columns: minmax(0, 1fr);
  }

  .claim-search-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .summary-content {
    justify-content: flex-start;
    text-align: left;
  }

  .claim-cluster-row {
    gap: 16px;
    grid-template-columns: minmax(0, 1fr);
    padding: 20px 62px 20px 18px;
  }

  .claim-provider-cell {
    grid-column: auto;
  }

  .claim-empty-state {
    align-items: flex-start;
    flex-direction: column;
    padding: 24px 20px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .claim-registry-toolbar,
  .claim-filter-shell,
  .claim-search-summary,
  .claim-cluster-section,
  .claim-empty-state {
    animation: none;
  }

  .claim-list-progress span {
    animation: none;
    width: 100%;
  }
}
</style>
